import type { Business, GroundingChunk } from '../types';

// In production, use same domain (empty string = relative URL)
// In development, use localhost:3001
const PROXY_URL = import.meta.env.VITE_PROXY_URL || (import.meta.env.PROD ? '' : 'http://localhost:3001');

interface GeminiResponse {
  text: string;
  candidates?: Array<{
    groundingMetadata?: {
      groundingChunks?: GroundingChunk[];
    };
  }>;
}

async function callGeminiProxy(
  model: string,
  contents: string,
  config?: any
): Promise<GeminiResponse> {
  const response = await fetch(`${PROXY_URL}/api/gemini/generate`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for authentication
    body: JSON.stringify({
      model,
      contents,
      config,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to call Gemini API through proxy');
  }

  return response.json();
}

export async function searchBusinesses(
  query: string,
  userLocation: { latitude: number; longitude: number } | null,
  resultLimit: string
): Promise<{ results: Business[]; count: number; method: string }> {
  try {
    // Call new Places API endpoint instead of Gemini JSON parsing
    const response = await fetch(`${PROXY_URL}/api/search-businesses`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Include cookies for authentication
      body: JSON.stringify({
        query,
        userLocation,
        resultLimit
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.details || 'Failed to search businesses');
    }

    const data = await response.json();

    return {
      results: data.results || [],
      count: data.count || 0,
      method: data.method || 'places-api'
    };

  } catch (error) {
    console.error("Error searching businesses:", error);
    // Surface the server's actual message. Reporting every failure as a Places
    // API error hid a Gemini auth outage behind the wrong subsystem.
    throw new Error(error instanceof Error ? error.message : "Failed to search businesses.");
  }
}

export async function enrichBusinessData(business: Business, options: string[]): Promise<Partial<Business>> {
    const prompt = `You are a data enrichment specialist. Your task is to find specific information for the given business using its name and address.
Use Google Search to find the following pieces of information: ${options.join(', ')}.
Return the results as a valid JSON object. The JSON object should only contain the following keys: ${options.map(o => `"${o}"`).join(', ')}.
If you cannot find a specific piece of information, use an empty string "" as the value.
Do not add any extra text, explanations, or markdown formatting (like \`\`\`json). Your entire response must be only the JSON object.

Business Name: "${business.name}"
Business Address: "${business.address}"`;

    try {
        const response = await callGeminiProxy('gemini-2.5-flash', prompt, {
            tools: [{ googleSearch: {} }],
        });

        let jsonString = response.text ? response.text.trim() : "{}";

        // Strip markdown fences just in case the model adds them despite instructions
        const match = jsonString.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
        if (match && match[1]) {
          jsonString = match[1];
        }

        return JSON.parse(jsonString);

    } catch (error) {
        console.error(`Error enriching data for ${business.name}:`, error);
        throw new Error(`Failed to enrich data for ${business.name}.`);
    }
}


export async function validateAddress(address: string): Promise<{ valid: boolean; address: string | null }> {
    const prompt = `You are an address validation expert. Given the following user input, determine if it's a specific, real-world address suitable for mapping.
    If it is, return a corrected and standardized version of the address.
    If it's not a valid or specific address (e.g., just "California" or "nearby"), indicate that it's invalid.
    User Input: "${address}"`;

    try {
        const response = await callGeminiProxy('gemini-2.5-flash', prompt, {
            responseMimeType: "application/json",
            responseSchema: {
                type: 'object',
                properties: {
                    valid: { type: 'boolean' },
                    address: { type: 'string' },
                },
            },
        });

        const jsonString = response.text ? response.text.trim() : "{}";
        const result = JSON.parse(jsonString);
        return {
            valid: result.valid === true,
            address: result.valid ? result.address : null
        };
    } catch (error) {
        console.error("Error validating address:", error);
        // On failure, assume invalid to prevent bad data from proceeding.
        return { valid: false, address: null };
    }
}


export async function optimizeRoute(
  addresses: string[],
  userLocation: { latitude: number; longitude: number } | null,
  startLocation: string | null,
  endLocation: string | null
): Promise<string[]> {
  let tripInfo = '';

  if (startLocation && endLocation) {
    tripInfo = `This is a one-way trip that must start at "${startLocation}" and must end at "${endLocation}".`;
  } else if (startLocation) {
    tripInfo = `This is a round trip that must start and end at "${startLocation}".`;
  } else if (userLocation) {
    tripInfo = `This is a round trip starting and ending at the user's current location: latitude ${userLocation.latitude}, longitude ${userLocation.longitude}.`;
  } else {
    tripInfo = `This is a round trip that should start and end at the first optimal location in the list.`;
  }


  const prompt = `You are a logistics expert specializing in solving the Traveling Salesperson Problem. Your task is to determine the most efficient driving route to visit a list of business addresses.
${tripInfo}
Analyze the following list of addresses and return them in the optimal order to minimize travel distance. The start and end locations should not be in the returned list unless they are also one of the business addresses to visit.
The response must be a valid JSON array of strings, where each string is one of the provided addresses to visit. The array should only contain the addresses in the calculated optimal order.
Do not include any other text, explanations, or markdown formatting in your response.

Addresses to visit:
${JSON.stringify(addresses)}
`;

  try {
    const response = await callGeminiProxy('gemini-2.5-pro', prompt, {
      responseMimeType: "application/json",
    });

    const jsonString = response.text ? response.text.trim() : "[]";
    const orderedAddresses = JSON.parse(jsonString);

    if (Array.isArray(orderedAddresses) && orderedAddresses.every(item => typeof item === 'string')) {
      return orderedAddresses as string[];
    } else {
      throw new Error("API returned an unexpected data format for the route.");
    }

  } catch (error) {
    console.error("Error optimizing route:", error);
    throw new Error("Failed to optimize the route using the Gemini API.");
  }
}
